import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, X } from 'lucide-react';
import { api, ApiError, type FieldPhase, type PhaseMap, type SpirePhase } from '../../lib/api.js';
import { useAuth } from '../../lib/auth-store.js';
import { cn } from '../../lib/cn.js';
import { phaseLabel } from '../../lib/field-time.js';
import { Button } from '../ui/button.js';
import { Feedback } from '../ui/feedback.js';
import { Input } from '../ui/input.js';

/** The job phases a tech moves through, in workflow order. `paused` is off-axis but still mappable. */
const JOB_PHASES: ReadonlyArray<{ key: FieldPhase; hint: string }> = [
  { key: 'scheduled', hint: 'Dispatched to a tech; nobody has started yet' },
  { key: 'traveling', hint: 'Tech tapped Start — on the way' },
  { key: 'on_site', hint: 'Tech arrived' },
  { key: 'work_in_progress', hint: 'Work started (also where Resume lands)' },
  { key: 'paused', hint: 'Tech paused the visit' },
  { key: 'work_completed', hint: 'Work done, awaiting signature' },
  { key: 'signed_off', hint: 'Clocked out and complete' },
];

const PHASE_MAP_KEY = ['settings', 'phase-map'] as const;
const SPIRE_PHASES_KEY = ['settings', 'spire-phases'] as const;

/**
 * Admin → Job phases. Maps each Tinsmith job phase to the Spire sales-order
 * phase the order should move to when a lead tech enters it. Unmapped
 * phases leave Spire's phase alone. The Spire phase list is the client's
 * own (Edit → Phases in Spire desktop) and is read live.
 */
export function PhaseMapPanel() {
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'admin';
  const queryClient = useQueryClient();

  const mapQuery = useQuery({
    queryKey: PHASE_MAP_KEY,
    queryFn: ({ signal }) => api.settings.phaseMap(signal),
    staleTime: 60_000,
  });
  const phasesQuery = useQuery({
    queryKey: SPIRE_PHASES_KEY,
    queryFn: ({ signal }) => api.settings.spirePhases(signal),
    staleTime: 60_000,
  });

  const saved = mapQuery.data;
  const [draft, setDraft] = useState<PhaseMap | null>(null);
  useEffect(() => {
    if (saved) setDraft(saved);
  }, [saved]);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation<PhaseMap, ApiError, PhaseMap>({
    mutationFn: (body) => api.settings.updatePhaseMap(body),
    onSuccess: async (result) => {
      queryClient.setQueryData(PHASE_MAP_KEY, result);
      setNotice('Saved. Phase changes from now on will update Spire.');
      setTimeout(() => setNotice(null), 4000);
    },
  });

  const spirePhases = phasesQuery.data ?? [];
  const dirty = !!saved && !!draft && JOB_PHASES.some((p) => draft[p.key] !== saved[p.key]);

  return (
    <section
      className="rounded-xl border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-labelledby="phase-map-title"
    >
      <div>
        <h2 id="phase-map-title" className="font-medium text-ink">
          Job phases → Spire phases
        </h2>
        <p className="text-xs text-ink-muted mt-0.5">
          When a lead tech moves a job into one of these phases, the order's Phase in Spire is set
          to the mapped value (Spire records it in the order's phase history). Leave one blank to
          leave Spire's phase alone at that step.
          {!canEdit && ' Only admins can change these.'}
        </p>
      </div>

      {phasesQuery.isError ? (
        <Feedback variant="error" size="sm" className="mt-3">
          Couldn't load the phases from Spire: {phasesQuery.error.message}
        </Feedback>
      ) : phasesQuery.isSuccess && spirePhases.length === 0 ? (
        <Feedback variant="warning" size="sm" className="mt-3">
          Spire has no sales-order phases defined yet. Set them up in Spire desktop (Edit → Phases,
          type Sales Order), then come back here.
        </Feedback>
      ) : null}

      <div className="mt-4 space-y-3">
        {JOB_PHASES.map(({ key, hint }) => (
          <div key={key} className="grid gap-1 sm:grid-cols-[200px_1fr] sm:items-center">
            <div>
              <div className="text-sm text-ink">{phaseLabel(key)}</div>
              <div className="text-[11px] text-ink-subtle">{hint}</div>
            </div>
            <SpirePhasePicker
              phases={spirePhases}
              value={draft?.[key] ?? ''}
              disabled={!canEdit || !draft}
              loading={phasesQuery.isPending}
              onChange={(code) => draft && setDraft({ ...draft, [key]: code })}
            />
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="mt-4 flex items-center gap-3">
          <Button size="sm" disabled={!dirty || save.isPending || !draft} onClick={() => draft && save.mutate(draft)}>
            {save.isPending ? 'Saving…' : 'Save mapping'}
          </Button>
          {dirty && !save.isPending && (
            <Button size="sm" variant="ghost" onClick={() => saved && setDraft(saved)}>
              Discard
            </Button>
          )}
          {notice && <span className="text-xs text-emerald-700">{notice}</span>}
        </div>
      )}
      {save.isError && (
        <Feedback variant="error" size="sm" className="mt-2">
          Couldn't save: {save.error.message}
        </Feedback>
      )}
    </section>
  );
}

/**
 * Searchable single-select over the Spire phases: type to filter by code or
 * description, arrow keys + Enter to pick, × to clear. Shows the chosen
 * phase as "CODE — description".
 */
function SpirePhasePicker({
  phases,
  value,
  disabled,
  loading,
  onChange,
}: {
  phases: ReadonlyArray<SpirePhase>;
  value: string;
  disabled: boolean;
  loading: boolean;
  onChange: (code: string) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const selected = phases.find((p) => p.phaseId === value) ?? null;
  const q = text.trim().toLowerCase();
  const matches = useMemo(
    () =>
      phases.filter(
        (p) => !q || p.phaseId.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q),
      ),
    [phases, q],
  );
  useEffect(() => setActive(0), [q]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const pick = (p: SpirePhase) => {
    onChange(p.phaseId);
    setText('');
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (matches.length ? (i + 1) % matches.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (matches.length ? (i - 1 + matches.length) % matches.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const m = matches[active];
      if (m) pick(m);
    }
  };

  // A saved code Spire no longer has (phase deleted in Spire) still shows,
  // flagged, so the admin can see and clear it.
  const stale = value && !selected;

  return (
    <div ref={rootRef} className="relative">
      <Input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Spire phase"
        disabled={disabled}
        placeholder={
          loading
            ? 'Loading Spire phases…'
            : selected
              ? `${selected.phaseId}${selected.description ? ` — ${selected.description}` : ''}`
              : stale
                ? `${value} (not in Spire any more)`
                : 'Not mapped — search Spire phases'
        }
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        spellCheck={false}
        className={cn('pr-16', (selected || stale) && !text && 'placeholder:text-ink placeholder:font-medium', stale && !text && 'placeholder:text-amber-700')}
      />
      <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
        {(value || text) && !disabled && (
          <button
            type="button"
            aria-label={text ? 'Clear search' : 'Unmap this phase'}
            onClick={() => {
              if (text) setText('');
              else onChange('');
              setOpen(false);
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-subtle hover:text-ink"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
        <ChevronDown className="h-4 w-4 text-ink-subtle mr-1" aria-hidden />
      </div>

      {open && !disabled && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1 left-0 right-0 max-h-64 overflow-auto rounded-lg border bg-surface shadow-lg"
          style={{ borderColor: 'var(--color-border-secondary)' }}
        >
          {phases.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-muted">No sales-order phases in Spire.</li>
          ) : matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-muted">No phases match "{text.trim()}".</li>
          ) : (
            matches.map((p, i) => (
              <li key={p.spireId} role="option" aria-selected={p.phaseId === value}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(p)}
                  className={cn(
                    'flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm',
                    i === active && 'bg-surface-muted',
                    p.phaseId === value && 'font-medium',
                  )}
                >
                  <span className="font-mono text-xs text-ink">{p.phaseId}</span>
                  {p.description && <span className="min-w-0 truncate text-ink-muted">{p.description}</span>}
                  {p.nextPhaseId && (
                    <span className="ml-auto shrink-0 text-[10px] text-ink-subtle">→ {p.nextPhaseId}</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
